import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { useSearchParams, useSelectedOrganizationContainer } from "../../../shared/hooks";
import { useDefaultOrganizationId } from "../../../shared/hooks/useDefaultOrganizationId";
import { FEATURE_ORG_ROUTE_MAP, Feature, LENS_FOLDER_FEATURE_ROUTE_MAP } from "../../../shared/models";
import { AbsoluteRoute } from "../absoluteRoutes";

export const useV3Routes = (): {
    goToContainerFeature: (feature: Feature, orgId: string, lensFolderId?: string) => void;
    goToSelectedOrgLensesInsights: () => void;
    goToCommunityAudience: () => void;
    goToWelcome: () => void;
    goToLensFolders: (orgId?: string) => void;
    goToAssetSubmission: () => void;
    goToCameraKit: () => void;
    goToHome: (orgId?: string) => void;
} => {
    const defaultOrganizationContainerId = useDefaultOrganizationId();
    const { orgId: selectedOrganizationId } = useSelectedOrganizationContainer();

    const navigate = useNavigate();
    const searchParams = useSearchParams();

    const goToContainerFeature = useCallback(
        (feature: Feature, orgId: string, lensFolderId?: string) => {
            const effectiveOrgId = orgId.length > 0 ? orgId : defaultOrganizationContainerId;
            let route = "";
            if (lensFolderId) {
                if (feature === Feature.lenses) {
                    route = generatePath(AbsoluteRoute.ORG_LENS_FOLDER_LENSES, {
                        orgId: effectiveOrgId,
                        lensFolderId,
                    });
                }
                const isDefaultOrg = effectiveOrgId === defaultOrganizationContainerId;
                const folderRoute = LENS_FOLDER_FEATURE_ROUTE_MAP[feature];
                const orgRouteForFeature = FEATURE_ORG_ROUTE_MAP[feature];
                if (folderRoute) {
                    route =
                        isDefaultOrg && orgRouteForFeature
                            ? generatePath(orgRouteForFeature, { orgId: effectiveOrgId })
                            : generatePath(folderRoute, { orgId: effectiveOrgId, lensFolderId });
                }
            } else {
                const orgRoute = FEATURE_ORG_ROUTE_MAP[feature];
                if (orgRoute) {
                    route = generatePath(orgRoute, { orgId: effectiveOrgId });
                }
            }
            navigate(route);
        },
        [navigate, defaultOrganizationContainerId],
    );

    const goToLensFolders = useCallback(
        (orgId?: string) => {
            if (orgId) {
                navigate(generatePath(AbsoluteRoute.ORG_LENS_FOLDERS, { orgId }));
            } else {
                navigate(generatePath(AbsoluteRoute.LENS_FOLDERS));
            }
        },
        [navigate],
    );

    const goToSelectedOrgLensesInsights = useCallback(() => {
        navigate(
            `${generatePath(AbsoluteRoute.ORG_INSIGHTS, {
                orgId: selectedOrganizationId ?? "",
            })}?${searchParams.toString()}`,
        );
    }, [navigate, selectedOrganizationId, searchParams]);

    const goToCommunityAudience = useCallback(() => {
        navigate(AbsoluteRoute.COMMUNITY_AUDIENCE);
    }, [navigate]);

    const goToWelcome = useCallback(() => {
        navigate(AbsoluteRoute.WELCOME);
    }, [navigate]);

    const goToAssetSubmission = useCallback(() => {
        navigate(
            generatePath(AbsoluteRoute.ORG_ASSETS_CREATE, {
                orgId: selectedOrganizationId ?? "",
            }),
        );
    }, [navigate, selectedOrganizationId]);

    const goToCameraKit = useCallback(() => {
        navigate(generatePath(AbsoluteRoute.CK_ROOT));
    }, [navigate]);

    const goToHome = useCallback(() => {
        navigate(AbsoluteRoute.HOME);
    }, [navigate]);

    return {
        goToContainerFeature,
        goToSelectedOrgLensesInsights,
        goToCommunityAudience,
        goToWelcome,
        goToLensFolders,
        goToAssetSubmission,
        goToCameraKit,
        goToHome,
    };
};
